// based on https://developers.google.com/web/fundamentals/design-and-ux/input/touch#implement-custom-gestures

import {RefObject, useCallback, useEffect, useRef} from "react";
import {useStateRef} from "@web2/react_utils";

import {SLIDER_ANIMATION_DURATION} from "../components/OfferBoxGallery";

const CLICK_PREVENTION_TIMEOUT_MS = 50; // when swipe is detected, we should block click events for some time
const DEADBAND = 10; // moves below this are considered 'not moving'

export enum GestureDirection {
    LEFT = 1,
    NONE = 0,
    RIGHT = -1
}

let timerTouchMove: NodeJS.Timeout;

export const useOfferboxTouchEvents = <T extends HTMLElement | undefined>(
    ref: RefObject<T>,
    onGestureStart: (width?: number) => void,
    onGestureEnd: (gestureDirection: GestureDirection) => void,
    isMobile: boolean | null
) => {
    const firstTouchPosition = useRef<number>(0);
    const lastTouchPositionRef = useRef<number>(0);
    const firstClientX = useRef<number>(0);

    // Ref below marks that we are currently waiting for the Request Animation Frame to execute. Used to debounce animations to a maximum of 60fps or whenever new animation frame executes
    const rafPendingRef = useRef(false);
    const clickPreventionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [shouldBlockEvents, setShouldBlockEvents, shouldBlockEventsRef] = useStateRef(false);
    const gestureDirectionRef = useRef<GestureDirection>(GestureDirection.NONE);
    const isSwipeDetectedRef = useRef(false);

    /*
     * define event handlers
     */

    // SAFARI MOBILE FIX - Below two event handlers are added to prevent vertical scrolling when user horizontally scrolls the slider

    const safariMobileFixPreventVerticalScroll = useCallback((event: TouchEvent | PointerEvent) => {
        const clientX = getGesturePosFromEvent(event) - firstClientX.current;

        // Vertical scrolling does not work when you start swiping horizontally.
        if (Math.abs(clientX) > DEADBAND) {
            event.preventDefault();
            return false;
        }
    }, []);

    const safariMobileFixTouchMoveStart = useCallback((e: TouchEvent | PointerEvent) => {
        firstClientX.current = getGesturePosFromEvent(e);
    }, []);

    // start gesture
    const handleGestureStart = useCallback((event: TouchEvent | PointerEvent | MouseEvent) => {
        event.preventDefault();
        if ((event as TouchEvent).touches?.length > 1) {
            return;
        }

        onGestureStart((event.currentTarget as T)?.clientWidth);
        firstTouchPosition.current = getGesturePosFromEvent(event);

        // reset new gesture
        lastTouchPositionRef.current = getGesturePosFromEvent(event);
        isSwipeDetectedRef.current = true;

        // prevent click actions during gestures
        clickPreventionTimeoutRef.current = setTimeout(() => setShouldBlockEvents(true), CLICK_PREVENTION_TIMEOUT_MS);
    }, []);

    // move gesture
    const handleGestureMove = useCallback((event: TouchEvent | PointerEvent | MouseEvent) => {
        event.preventDefault();

        if (rafPendingRef.current) {
            // wait for next animation frame
            return;
        }

        lastTouchPositionRef.current = getGesturePosFromEvent(event);

        const differenceInX =
            Number.isFinite(firstTouchPosition.current) && lastTouchPositionRef.current ? firstTouchPosition.current - lastTouchPositionRef.current : 0;

        gestureDirectionRef.current =
            differenceInX > DEADBAND ? GestureDirection.LEFT : Math.abs(differenceInX) < DEADBAND ? GestureDirection.NONE : GestureDirection.RIGHT;

        if (isSwipeDetectedRef.current && gestureDirectionRef.current) {
            timerTouchMove = setTimeout(() => {
                onGestureEnd(gestureDirectionRef.current);
                gestureDirectionRef.current = GestureDirection.NONE;
                isSwipeDetectedRef.current = false;
            }, SLIDER_ANIMATION_DURATION / 3);
        }
    }, []);

    // end gesture
    const handleGestureEnd = useCallback((event: TouchEvent | PointerEvent | MouseEvent) => {
        event.preventDefault();

        if ((event as TouchEvent).touches && (event as TouchEvent).touches.length > 0) {
            return;
        }

        clickPreventionTimeoutRef.current && clearTimeout(clickPreventionTimeoutRef.current);
        setShouldBlockEvents(false);
    }, []);

    /*
     * attach/detach event listeners
     */
    useEffect(() => {
        if (!ref.current || !isMobile) {
            return;
        }
        // Check if pointer events are supported.
        if (window.PointerEvent) {
            // Add Pointer Event Listener
            ref.current.addEventListener("pointerdown", safariMobileFixTouchMoveStart);
            ref.current.addEventListener("pointermove", safariMobileFixPreventVerticalScroll, {
                passive: false
            });
            ref.current.addEventListener("pointerdown", handleGestureStart, true);
            ref.current.addEventListener("pointermove", handleGestureMove, true);
            ref.current.addEventListener("pointerup", handleGestureEnd, true);
            ref.current.addEventListener("pointercancel", handleGestureEnd, true);
        } else {
            // Add Touch Listener
            ref.current.addEventListener("touchstart", safariMobileFixTouchMoveStart);
            ref.current.addEventListener("touchmove", safariMobileFixPreventVerticalScroll, {
                passive: false
            });
            ref.current.addEventListener("touchstart", handleGestureStart, true);
            ref.current.addEventListener("touchmove", handleGestureMove, true);
            ref.current.addEventListener("touchend", handleGestureEnd, true);
            ref.current.addEventListener("touchcancel", handleGestureEnd, true);
            // Add Mouse Listener
            ref.current.addEventListener("mousedown", handleGestureStart, true);
        }

        return () => {
            clearTimeout(timerTouchMove);
            if (!ref.current || !isMobile) {
                return;
            }

            // unmount event cleanup
            if (window.PointerEvent) {
                ref.current.removeEventListener("pointerdown", safariMobileFixTouchMoveStart);
                ref.current.removeEventListener("pointermove", safariMobileFixPreventVerticalScroll);
                ref.current.removeEventListener("pointerdown", handleGestureStart, true);
                ref.current.removeEventListener("pointermove", handleGestureMove, true);
                ref.current.removeEventListener("pointerup", handleGestureEnd, true);
                ref.current.removeEventListener("pointercancel", handleGestureEnd, true);
            } else {
                ref.current.removeEventListener("touchstart", safariMobileFixTouchMoveStart);
                ref.current.removeEventListener("touchmove", safariMobileFixPreventVerticalScroll);
                ref.current.removeEventListener("touchstart", handleGestureStart, true);
                ref.current.removeEventListener("touchmove", handleGestureMove, true);
                ref.current.removeEventListener("touchend", handleGestureEnd, true);
                ref.current.removeEventListener("touchcancel", handleGestureEnd, true);

                ref.current.removeEventListener("mousedown", handleGestureStart, true);
            }
        };
    }, [isMobile]);

    return {shouldBlockEventsRef};
};

export function getGesturePosFromEvent(e: TouchEvent | MouseEvent | PointerEvent) {
    let xPos: number;

    if ((e as TouchEvent).targetTouches) {
        xPos = (e as TouchEvent).targetTouches[0].clientX;
    } else {
        xPos = (e as MouseEvent | PointerEvent).clientX;
    }
    return xPos;
}
